/**
 * Wave 4 stage K (analytics): the client event layer. Self-hosted and anonymous; see
 * docs/TECHNICAL.md#analytics-service and the privacy page (#/privacy).
 *
 * Every function here is a cheap no-op until `initAnalytics()` ran on a page where analytics are
 * allowed (never in tests or on the dev server, see env.ts) and the server said it is on. Call
 * sites are one-liners: `trackFunnel("spawn")`, `trackFeature("build:city")`, ...
 */
import { signal } from "@preact/signals";
import type { AnalyticsEvent, FunnelStep } from "../../shared/analytics";
import { readString, removeKey, writeString } from "../storage/local";
import { anonId, consent, setConsent, syncConsent, type ConsentChoice } from "./choice";
import { analyticsAllowed, detectBrowser, detectDevice, detectSource } from "./env";
import { AnalyticsQueue } from "./queue";

export { consent, type ConsentChoice } from "./choice";

const COLLECT_URL = "/api/pulse/collect";
const FORGET_URL = "/api/pulse/forget";
const CONFIG_URL = "/api/pulse/config";
const DEV_OVERRIDE_KEY = "conquer.analytics.dev";
/** An id whose deletion the server has not confirmed yet (retried on the next start). */
const PENDING_FORGET_KEY = "analytics.forget";
const MAX_ERRORS_PER_PAGE = 5;
/** On a first visit the first batch waits this long for the consent answer (first-touch source). */
const CONSENT_HOLD_MS = 120_000;

/** True once the server confirmed analytics are on for this page (the consent question shows). */
export const analyticsEnabled = signal(false);

let queue: AnalyticsQueue | null = null;
const funnelDone = new Set<FunnelStep>();
const pageHideHooks = new Set<() => void>();
const errorsSeen = new Set<string>();
let device: ReturnType<typeof detectDevice> = "desktop";
let browser: ReturnType<typeof detectBrowser> = "other";

function post(url: string, body: string): void {
  try {
    void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true, credentials: "omit" }).catch(() => undefined);
  } catch {
    /* never let analytics break the game */
  }
}

function devOverride(search: string): boolean {
  try {
    if (/(?:^|[?&])analytics=on\b/.test(search)) sessionStorage.setItem(DEV_OVERRIDE_KEY, "1");
    if (/(?:^|[?&])analytics=off\b/.test(search)) sessionStorage.removeItem(DEV_OVERRIDE_KEY);
    return sessionStorage.getItem(DEV_OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Start the event layer (main.tsx, once). */
export function initAnalytics(): void {
  if (queue || typeof window === "undefined") return;
  const search = location.search;
  const allowed = analyticsAllowed({ mode: import.meta.env.MODE, dev: !!import.meta.env.DEV, search, devOverride: devOverride(search) });
  if (!allowed) return;
  const ua = navigator.userAgent || "";
  device = detectDevice(ua, navigator.maxTouchPoints || 0, Math.min(screen.width || 0, screen.height || 0));
  browser = detectBrowser(ua);
  queue = new AnalyticsQueue({
    transport: {
      send: (body) => post(COLLECT_URL, body),
      beacon: (body) => {
        try {
          return typeof navigator.sendBeacon === "function" && navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: "application/json" }));
        } catch {
          return false;
        }
      },
    },
    identity: () => {
      const pid = anonId();
      return { consent: pid !== null, pid };
    },
  });
  if (consent.value === null) queue.hold(CONSENT_HOLD_MS);
  let inIframe = false;
  try {
    inIframe = window.top !== window.self;
  } catch {
    inIframe = true;
  }
  const lang = (document.documentElement.lang || navigator.language || "en").slice(0, 2).toLowerCase();
  queue.push({ e: "session", src: detectSource(search, document.referrer, inIframe, location.hostname), dev: device, br: browser, lang: /^[a-z]{2}$/.test(lang) ? lang : "en" });
  trackFunnel("visit");

  const onHide = () => queue?.flushOnHide();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onHide();
  });
  // The page is going away (tab closed, reload, navigation): last events first, then the beacon.
  window.addEventListener("pagehide", () => {
    runPageHideHooks();
    onHide();
  });
  // A choice made in another tab (the consent toggle there) applies here at once.
  window.addEventListener("storage", () => syncConsent());
  window.addEventListener("error", (e) => trackError("window", e.message || String(e.error ?? "")));
  window.addEventListener("unhandledrejection", (e) => trackError("promise", e.reason instanceof Error ? e.reason.message : String(e.reason ?? "")));

  void fetch(CONFIG_URL, { credentials: "omit", cache: "no-store" })
    .then((r) => (r.ok ? (r.json() as Promise<{ enabled?: unknown }>) : { enabled: false }))
    .catch(() => ({ enabled: false }))
    .then((cfg) => {
      const on = cfg.enabled === true;
      queue?.setEnabled(on);
      analyticsEnabled.value = on;
      if (on) {
        scheduleLoadTime();
        sendPendingForget();
      }
    });
}

function scheduleLoadTime(): void {
  const measure = () => {
    try {
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      const ms = nav && nav.loadEventEnd > 0 ? nav.loadEventEnd : performance.now();
      trackEvent({ e: "tech", dev: device, br: browser, load: Math.round(ms) });
    } catch {
      /* ignore */
    }
  };
  if (document.readyState === "complete") setTimeout(measure, 0);
  else window.addEventListener("load", () => setTimeout(measure, 0), { once: true });
}

/**
 * Run `fn` when the page is being closed or reloaded, just before the last events are handed to
 * sendBeacon (so events queued by `fn` still go out). Returns the unsubscribe function.
 */
export function onPageHide(fn: () => void): () => void {
  pageHideHooks.add(fn);
  return () => {
    pageHideHooks.delete(fn);
  };
}

function runPageHideHooks(): void {
  for (const fn of [...pageHideHooks]) {
    try {
      fn();
    } catch {
      /* never let analytics break the page */
    }
  }
}

/** Queue one event (validated again on the server). */
export function trackEvent(ev: AnalyticsEvent): void {
  queue?.push(ev);
}

/** A funnel step (sent at most once per page load). */
export function trackFunnel(step: FunnelStep): void {
  if (!queue || funnelDone.has(step)) return;
  funnelDone.add(step);
  queue.push({ e: "funnel", step });
}

/** One use of a feature (button, structure, weapon, overlay, shortcut, ...); summed per flush. */
export function trackFeature(id: string, n = 1): void {
  queue?.feature(id, n);
}

/** Wrap a keyboard handler so a handled key counts as a use of `key:<action>`. */
export function trackedKey<A extends unknown[], R>(action: string, handler: (...args: A) => R): (...args: A) => R {
  return (...args: A) => {
    const r = handler(...args);
    if ((r as unknown) !== false) queue?.feature(`key:${action}`);
    return r;
  };
}

export function trackCampaign(level: string, act: "start" | "finish" | "fail" | "hint", objective?: string): void {
  queue?.push(objective ? { e: "campaign", lvl: level, act, obj: objective } : { e: "campaign", lvl: level, act });
}

/** A performance sample during a game (frames per second, simulation tick time in ms). */
export function trackTech(sample: { fps?: number; tick?: number }): void {
  if (!queue || (sample.fps === undefined && sample.tick === undefined)) return;
  queue.push({ e: "tech", dev: device, br: browser, ...sample });
}

/** A client error (deduplicated, at most a few per page load; the server sanitises again). */
export function trackError(where: "window" | "promise" | "worker", message: string): void {
  if (!queue || !message || errorsSeen.size >= MAX_ERRORS_PER_PAGE || errorsSeen.has(message)) return;
  errorsSeen.add(message);
  queue.push({ e: "error", dev: device, br: browser, where, msg: message.slice(0, 300) });
}

/** Ask the server to delete a withdrawn id; kept until the server confirmed it. */
function sendPendingForget(): void {
  const pid = readString(PENDING_FORGET_KEY);
  if (!pid || queue?.isEnabled !== true) return;
  try {
    void fetch(FORGET_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pid }), keepalive: true, credentials: "omit" })
      .then((r) => {
        // 400 = not a valid id: nothing to retry.
        if ((r.ok || r.status === 400) && readString(PENDING_FORGET_KEY) === pid) removeKey(PENDING_FORGET_KEY);
      })
      .catch(() => undefined);
  } catch {
    /* retried on the next start */
  }
}

/** The player's answer to the consent question (or the settings toggle). */
export function chooseConsent(choice: ConsentChoice): void {
  const dropped = setConsent(choice);
  queue?.release();
  if (!dropped) return;
  writeString(PENDING_FORGET_KEY, dropped);
  sendPendingForget();
}

/** Debug / tests: the queue counters (null while analytics are off on this page). */
export function analyticsStats(): AnalyticsQueue["stats"] | null {
  return queue?.stats ?? null;
}
